import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Youtube } from 'lucide-react';
import { useNavProducts } from '@/hooks';
import { ROUTES, SOCIAL_LINKS } from '@/utils/constants';
import { openAskSanjoyWidget } from '@/utils';

const FOOTER_SOCIAL = [
  { href: SOCIAL_LINKS.linkedin, label: 'LinkedIn', Icon: Linkedin },
  { href: SOCIAL_LINKS.twitter, label: 'Twitter', Icon: Twitter },
  { href: SOCIAL_LINKS.youtube, label: 'YouTube', Icon: Youtube },
].filter((item) => Boolean(item.href));

const EXPLORE_LINKS = [
  { label: 'Knowledge', path: ROUTES.KNOWLEDGE },
  { label: 'Workflows', path: ROUTES.WORKFLOWS },
  { label: 'Products', path: ROUTES.PRODUCTS },
  { label: 'Ask Sanjoy', path: ROUTES.ASK, action: 'ask' as const },
];

const COMPANY_LINKS = [
  { label: 'About', path: ROUTES.ABOUT },
  { label: 'Team', path: ROUTES.TEAM },
  { label: 'Associate Program', path: ROUTES.ASSOCIATE_PROGRAM },
  { label: 'Partners', path: ROUTES.PARTNERS },
  { label: 'Resources', path: ROUTES.RESOURCES },
];

export function Footer() {
  const { data: products = [] } = useNavProducts();

  return (
    <footer className="border-t border-[var(--sj-border)] bg-[var(--sj-surface-muted)]">
      {/* Mobile: minimal legal strip */}
      <div className="mx-auto max-w-[1280px] px-6 py-6 lg:hidden">
        <p className="text-center text-xs text-[var(--sj-text-muted)]">
          &copy; {new Date().getFullYear()} Sanjoy.ai. All rights reserved.
        </p>
      </div>

      {/* Desktop: full footer */}
      <div className="mx-auto hidden max-w-[1280px] px-6 py-16 lg:block">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="inline-block">
              <img src="/sj_light.png" alt="Sanjoy" className="h-8 w-auto" />
            </Link>
            <p className="mt-3 text-sm leading-relaxed text-[var(--sj-text-muted)]">
              Helping businesses understand, adopt, and implement Artificial Intelligence with confidence.
            </p>
            <p className="mt-2 text-xs font-medium text-[var(--sj-text-secondary)]">
              Make It Happen.
            </p>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--sj-text-primary)]">
              Explore
            </h3>
            <ul className="mt-4 flex flex-col gap-2.5">
              {EXPLORE_LINKS.map((link) => (
                <li key={link.path}>
                  {'action' in link && link.action === 'ask' ? (
                    <button
                      type="button"
                      onClick={openAskSanjoyWidget}
                      className="text-sm text-[var(--sj-text-muted)] transition-colors hover:text-[var(--sj-text-primary)]"
                    >
                      {link.label}
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      className="text-sm text-[var(--sj-text-muted)] transition-colors hover:text-[var(--sj-text-primary)]"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--sj-text-primary)]">
              Company
            </h3>
            <ul className="mt-4 flex flex-col gap-2.5">
              {COMPANY_LINKS.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-[var(--sj-text-muted)] transition-colors hover:text-[var(--sj-text-primary)]"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--sj-text-primary)]">
              Products
            </h3>
            <ul className="mt-4 flex flex-col gap-2.5">
              {products.map((product) => (
                <li key={product.slug}>
                  <Link
                    to={`${ROUTES.PRODUCTS}/${product.slug}`}
                    className="inline-flex items-center gap-2 text-sm text-[var(--sj-text-muted)] transition-colors hover:text-[var(--sj-text-primary)]"
                  >
                    {product.logo_url ? (
                      <img
                        src={product.logo_url}
                        alt=""
                        className="h-4 w-4 shrink-0 rounded-[4px] object-contain"
                      />
                    ) : product.icon ? (
                      <span className="text-xs leading-none" aria-hidden>
                        {product.icon}
                      </span>
                    ) : null}
                    {product.title}
                  </Link>
                </li>
              ))}
              {products.length === 0 && (
                <li className="text-sm text-[var(--sj-text-muted)]">Coming soon</li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-[var(--sj-border)] pt-8 sm:flex-row">
          <p className="text-xs text-[var(--sj-text-muted)]">
            &copy; {new Date().getFullYear()} Sanjoy.ai. All rights reserved.
          </p>

          {FOOTER_SOCIAL.length > 0 && (
            <div className="flex items-center gap-3">
              {FOOTER_SOCIAL.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-[6px] p-1.5 text-[var(--sj-text-muted)] transition-colors hover:text-[var(--sj-text-primary)] hover:bg-[var(--sj-surface)]"
                  aria-label={label}
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
